import { useState } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, ShieldAlert, CheckCircle2, RefreshCw, AlertTriangle, Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ComplianceAlert {
  id: number;
  alertType: "duplicate_tin" | "self_dealing" | "duplicate_creditor_account" | "ip_velocity" | "device_fingerprint_collision" | "duplicate_identity" | "tin_mismatch";
  status: "open" | "resolved";
  detectedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

function shortDate(d: string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const TYPE_CONFIG = {
  duplicate_tin: {
    label: "Duplicate Tax ID",
    color: "bg-red-100 text-red-800 border-red-300",
    description: "The same SSN/ITIN was submitted on more than one account. This may indicate the same person created multiple debtor accounts.",
    icon: <ShieldAlert className="h-4 w-4" />,
  },
  self_dealing: {
    label: "Potential Self-Dealing",
    color: "bg-orange-100 text-orange-800 border-orange-300",
    description: "A donor completed a donation to a pool that contains debt from an account sharing their email address. IRS rules prohibit private benefit to 501(c)(3) donors.",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  duplicate_creditor_account: {
    label: "Duplicate Creditor Account",
    color: "bg-purple-100 text-purple-800 border-purple-300",
    description: "A debt submission was blocked because the creditor account number is already on file for a different user. This may indicate an attempt to claim someone else's debt.",
    icon: <ShieldAlert className="h-4 w-4" />,
  },
  ip_velocity: {
    label: "IP Velocity",
    color: "bg-rose-100 text-rose-800 border-rose-300",
    description: "More than 3 new accounts were created from the same IP address within 24 hours. This may indicate automated account creation or coordinated abuse.",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  device_fingerprint_collision: {
    label: "Device Fingerprint Match",
    color: "bg-amber-100 text-amber-800 border-amber-300",
    description: "More than 2 accounts share the same device fingerprint. This may indicate the same person creating multiple accounts to bypass debt limits or identity checks.",
    icon: <ShieldAlert className="h-4 w-4" />,
  },
  duplicate_identity: {
    label: "Duplicate Identity",
    color: "bg-red-100 text-red-800 border-red-300",
    description: "A new debtor submitted the same legal name and date of birth as an existing verified account. The new account has been automatically suspended pending your review.",
    icon: <ShieldAlert className="h-4 w-4" />,
  },
  tin_mismatch: {
    label: "TIN Mismatch",
    color: "bg-orange-100 text-orange-800 border-orange-300",
    description: "The IRS TIN Matching program returned a non-zero result code for this debtor's SSN/ITIN. The TIN may be invalid, not on file with the IRS, or the name does not match. Contact the debtor to obtain a corrected TIN before filing their 1099-C.",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
};

function MetadataPanel({ alert }: { alert: ComplianceAlert }) {
  const m = alert.metadata ?? {};
  if (alert.alertType === "duplicate_tin") {
    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p><span className="font-medium text-foreground">New account:</span> {String(m.newUserEmail ?? "—")} (ID: {String(m.newUserId ?? "—")})</p>
        {Array.isArray(m.conflictingUsers) && (
          <div>
            <span className="font-medium text-foreground">Conflict with:</span>
            <ul className="ml-3 mt-0.5 space-y-0.5">
              {(m.conflictingUsers as Array<{ id: number; email: string }>).map((u) => (
                <li key={u.id}>{u.email ?? "—"} (ID: {u.id})</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
  if (alert.alertType === "self_dealing") {
    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p><span className="font-medium text-foreground">Donor:</span> {String(m.donorEmail ?? "—")} (ID: {String(m.donorUserId ?? "—")})</p>
        <p><span className="font-medium text-foreground">Pool:</span> {String(m.poolName ?? m.poolId ?? "—")}</p>
        <p><span className="font-medium text-foreground">Donation ID:</span> #{String(m.donationId ?? "—")}</p>
        {Array.isArray(m.conflictingDebtIds) && (
          <p><span className="font-medium text-foreground">Conflicting debt IDs:</span> {(m.conflictingDebtIds as number[]).map((id) => `#${String(id).padStart(8, "0")}`).join(", ")}</p>
        )}
      </div>
    );
  }
  if (alert.alertType === "duplicate_creditor_account") {
    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p><span className="font-medium text-foreground">Creditor:</span> {String(m.creditor ?? "—")}</p>
        <p><span className="font-medium text-foreground">Account number (normalized):</span> <code className="bg-muted px-1 rounded">{String(m.accountNumberNormalized ?? "—")}</code></p>
        <p><span className="font-medium text-foreground">Blocked user:</span> {String(m.attemptingUserEmail ?? "—")} (ID: {String(m.attemptingUserId ?? "—")})</p>
        <p><span className="font-medium text-foreground">Existing user:</span> {String(m.existingUserEmail ?? "—")} (ID: {String(m.existingUserId ?? "—")})</p>
        <p><span className="font-medium text-foreground">Existing debt:</span> #{String(m.existingDebtId ?? "—").padStart(8, "0")}</p>
      </div>
    );
  }
  if (alert.alertType === "ip_velocity") {
    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p><span className="font-medium text-foreground">IP address:</span> <code className="bg-muted px-1 rounded">{String(m.ip ?? "—")}</code></p>
        <p><span className="font-medium text-foreground">Accounts in window:</span> {String(m.count ?? "—")} (threshold: {String(m.threshold ?? 3)}, window: {String(m.windowHours ?? 24)}h)</p>
        <p>
          <span className="font-medium text-foreground">Triggering account:</span>{" "}
          {m.triggeringUserEmail
            ? <a href={`mailto:${String(m.triggeringUserEmail)}`} className="text-blue-600 hover:underline">{String(m.triggeringUserEmail)}</a>
            : "—"}{" "}(ID: {String(m.triggeringUserId ?? "—")})
        </p>
        {Array.isArray(m.affectedUsers) && (
          <div>
            <span className="font-medium text-foreground">All affected accounts:</span>
            <ul className="ml-3 mt-0.5 space-y-0.5">
              {(m.affectedUsers as Array<{ id: number; email: string; role: string; createdAt: string }>).map((u) => (
                <li key={u.id}>
                  <a href={`mailto:${u.email}`} className="text-blue-600 hover:underline">{u.email ?? "—"}</a>
                  {" "}(ID: {u.id}, role: {u.role})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
  if (alert.alertType === "device_fingerprint_collision") {
    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p><span className="font-medium text-foreground">Device fingerprint:</span> <code className="bg-muted px-1 rounded">{String(m.deviceFingerprintHash ?? "—")}</code></p>
        <p><span className="font-medium text-foreground">Accounts sharing fingerprint:</span> {String(m.count ?? "—")} (threshold: {String(m.threshold ?? 2)})</p>
        <p>
          <span className="font-medium text-foreground">Triggering account:</span>{" "}
          {m.triggeringUserEmail
            ? <a href={`mailto:${String(m.triggeringUserEmail)}`} className="text-blue-600 hover:underline">{String(m.triggeringUserEmail)}</a>
            : "—"}{" "}(ID: {String(m.triggeringUserId ?? "—")})
        </p>
        {Array.isArray(m.affectedUsers) && (
          <div>
            <span className="font-medium text-foreground">All accounts from this device:</span>
            <ul className="ml-3 mt-0.5 space-y-0.5">
              {(m.affectedUsers as Array<{ id: number; email: string; role: string; createdAt: string }>).map((u) => (
                <li key={u.id}>
                  <a href={`mailto:${u.email}`} className="text-blue-600 hover:underline">{u.email ?? "—"}</a>
                  {" "}(ID: {u.id}, role: {u.role})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
  if (alert.alertType === "duplicate_identity") {
    type IdentityFields = {
      id?: number; email?: string;
      legalFirstName?: string; legalLastName?: string; dateOfBirth?: string;
      addressLine1?: string; addressCity?: string; addressState?: string; addressZip?: string;
    };
    const newId = m.newUserIdentity as IdentityFields | undefined;
    const conflicts = (m.conflictingUsers as IdentityFields[] | undefined) ?? [];
    const matchPaths = Array.isArray(m.matchPaths) ? (m.matchPaths as string[]).join(", ") : null;
    const IdentityBlock = ({ label, u }: { label: string; u: IdentityFields }) => (
      <div className="rounded border border-muted bg-muted/30 p-2 space-y-0.5">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {u.email && <p><span className="font-medium">Email:</span> {u.email}{u.id ? ` (ID: ${u.id})` : ""}</p>}
        {(u.legalFirstName || u.legalLastName) && <p><span className="font-medium">Name:</span> {[u.legalFirstName, u.legalLastName].filter(Boolean).join(" ")}</p>}
        {u.dateOfBirth && <p><span className="font-medium">DOB:</span> {u.dateOfBirth}</p>}
        {u.addressLine1 && <p><span className="font-medium">Address:</span> {[u.addressLine1, u.addressCity, u.addressState, u.addressZip].filter(Boolean).join(", ")}</p>}
      </div>
    );
    return (
      <div className="mt-3 space-y-2 text-xs text-muted-foreground">
        {matchPaths && (
          <p><span className="font-medium text-foreground">Match detected on:</span>{" "}
            <span className="font-mono bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded text-amber-800">{matchPaths}</span>
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <IdentityBlock
            label={`Suspended account (ID: ${String(m.newUserId ?? "—")})`}
            u={{ email: String(m.newUserEmail ?? ""), ...(newId ?? {}) }}
          />
          {conflicts.map((u) => (
            <IdentityBlock key={u.id} label={`Existing account (ID: ${String(u.id ?? "—")})`} u={u} />
          ))}
          {conflicts.length === 0 && (
            <div className="rounded border border-muted bg-muted/30 p-2 text-muted-foreground italic">No existing account details</div>
          )}
        </div>
      </div>
    );
  }
  if (!alert.metadata || Object.keys(alert.metadata).length === 0) {
    return (
      <div className="mt-3 rounded border border-muted bg-muted/30 p-3 text-xs text-muted-foreground italic">
        No additional detail available for this alert type.
      </div>
    );
  }

  if (alert.alertType === "tin_mismatch") {
    const resultCode = String(m.resultCode ?? "");
    const resultDescription = String(m.resultDescription ?? "—");
    return (
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">IRS Result Code:</span>{" "}
          <code className="bg-muted px-1 rounded font-mono">{resultCode || "—"}</code>{" "}
          — {resultDescription}
        </p>
        <p><span className="font-medium text-foreground">Debtor:</span>{" "}
          {m.displayName ? `${String(m.displayName)} ` : ""}
          {m.legalFirstName || m.legalLastName
            ? `(${[m.legalFirstName, m.legalLastName].filter(Boolean).join(" ")})`
            : ""}
        </p>
        <p><span className="font-medium text-foreground">Email:</span>{" "}
          {m.userEmail
            ? <a href={`mailto:${String(m.userEmail)}`} className="text-blue-600 hover:underline">{String(m.userEmail)}</a>
            : "—"}
          {m.userId ? ` (ID: ${String(m.userId)})` : ""}
        </p>
        {m.payeeName && (
          <p><span className="font-medium text-foreground">Name submitted to IRS:</span> {String(m.payeeName)}</p>
        )}
      </div>
    );
  }
  return (
    <div className="mt-3 rounded border border-muted bg-muted/30 p-3 text-xs text-muted-foreground italic">
      No additional detail available for this alert type.
    </div>
  );
}

export default function ComplianceAlerts() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [resolveNotes, setResolveNotes] = useState<Record<number, string>>({});
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | "resolved">("open");

  const alertsQ = useQuery({
    queryKey: ["compliance-alerts", statusFilter],
    queryFn: async () => {
      const qs = `?status=${statusFilter}`;
      const r = await fetch(`/api/admin/compliance-alerts${qs}`, { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<{ alerts: ComplianceAlert[]; openCount: number; total: number }>;
    },
    refetchInterval: 30_000,
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes?: string }) => {
      const r = await fetch(`/api/admin/compliance-alerts/${id}/resolve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ["compliance-alerts"] });
      toast({ title: "Alert resolved", description: "The compliance alert has been marked as resolved." });
      setResolveNotes((prev) => { const n = { ...prev }; delete n[id]; return n; });
    },
    onError: () => {
      toast({ title: "Failed to resolve alert", variant: "destructive" });
    },
  });

  const clearSuspensionMutation = useMutation({
    mutationFn: async ({ userId, alertId, notes }: { userId: number; alertId: number; notes?: string }) => {
      const r = await fetch(`/api/admin/users/${userId}/clear-suspension`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      });
      if (!r.ok) throw new Error(await r.text());
      await fetch(`/api/admin/compliance-alerts/${alertId}/resolve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: notes ?? "Suspension cleared — identity verified by admin" }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-alerts"] });
      toast({ title: "Suspension cleared", description: "The account has been reinstated and the alert resolved." });
    },
    onError: () => {
      toast({ title: "Failed to clear suspension", variant: "destructive" });
    },
  });

  const terminateMutation = useMutation({
    mutationFn: async ({ userId, alertId, notes }: { userId: number; alertId: number; notes?: string }) => {
      const r = await fetch(`/api/admin/users/${userId}/terminate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      });
      if (!r.ok) throw new Error(await r.text());
      await fetch(`/api/admin/compliance-alerts/${alertId}/resolve`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: notes ?? "Account terminated — duplicate identity confirmed" }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-alerts"] });
      toast({ title: "Account terminated", description: "All debts rejected and the alert resolved." });
    },
    onError: () => {
      toast({ title: "Failed to terminate account", variant: "destructive" });
    },
  });

  const alerts = alertsQ.data?.alerts ?? [];
  const openCount = alertsQ.data?.openCount ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <LogoMark size={26} />
            <span className="text-sm font-semibold text-white">Compliance Alerts</span>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
            {openCount > 0 && (
              <Badge className="bg-red-100 text-red-800 border-red-300 text-xs font-bold ml-1">
                {openCount} open
              </Badge>
            )}
          </div>
          <UserButton />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Compliance Alerts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Automatically detected identity-integrity violations: duplicate Tax IDs and potential self-dealing transactions.
            Review each alert and resolve after investigation.
          </p>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-1">
            <p className="font-semibold">IRS 501(c)(3) Compliance Notice</p>
            <p>
              <strong>Duplicate TIN</strong> alerts indicate the same SSN/ITIN appeared on multiple accounts — possible account fraud.
              <strong> Self-dealing</strong> alerts indicate a donor may have donated to a pool containing their own debt,
              which constitutes prohibited private benefit under IRC §501(c)(3). Investigate all open alerts promptly.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {(["open", "resolved", "all"] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={statusFilter === f ? "default" : "outline"}
              onClick={() => setStatusFilter(f)}
              className="capitalize"
            >
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
          {alertsQ.isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-2" />}
        </div>

        {alertsQ.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-green-800">
              {statusFilter === "open" ? "No open compliance alerts — great!" : "No alerts found."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => {
              const cfg = TYPE_CONFIG[alert.alertType];
              return (
                <div
                  key={alert.id}
                  className={`rounded-lg border p-5 space-y-2 ${alert.status === "resolved" ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-xs font-semibold ${cfg.color} flex items-center gap-1`}>
                        {cfg.icon}
                        {cfg.label}
                      </Badge>
                      <Badge variant="outline" className={`text-xs ${alert.status === "open" ? "bg-yellow-50 text-yellow-800 border-yellow-300" : "bg-gray-100 text-gray-600"}`}>
                        {alert.status === "open" ? "Open" : "Resolved"}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{shortDate(alert.detectedAt)}</span>
                  </div>

                  <p className="text-sm text-muted-foreground">{cfg.description}</p>
                  <MetadataPanel alert={alert} />

                  {alert.status === "resolved" && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Resolved {shortDate(alert.resolvedAt)}
                      {alert.notes ? ` · "${alert.notes}"` : ""}
                    </p>
                  )}

                  {alert.status === "open" && (
                    <div className="pt-3 border-t space-y-2">
                      <input
                        type="text"
                        className="w-full text-sm border rounded px-3 py-1.5 bg-background"
                        placeholder="Resolution notes (optional)"
                        value={resolveNotes[alert.id] ?? ""}
                        onChange={(e) => setResolveNotes((prev) => ({ ...prev, [alert.id]: e.target.value }))}
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        {(alert.alertType === "duplicate_identity" || alert.alertType === "duplicate_tin") ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1.5 border-green-300 text-green-700 hover:bg-green-50"
                              onClick={() => {
                                const userId = Number(alert.metadata?.newUserId ?? 0);
                                if (!userId) return;
                                clearSuspensionMutation.mutate({ userId, alertId: alert.id, notes: resolveNotes[alert.id] });
                              }}
                              disabled={clearSuspensionMutation.isPending || terminateMutation.isPending}
                            >
                              {clearSuspensionMutation.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                              Clear Suspension
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="gap-1.5"
                              onClick={() => {
                                const userId = Number(alert.metadata?.newUserId ?? 0);
                                if (!userId) return;
                                if (!window.confirm(`Terminate account ID ${userId}? This will reject all their pending/queued debts. This cannot be undone.`)) return;
                                terminateMutation.mutate({ userId, alertId: alert.id, notes: resolveNotes[alert.id] });
                              }}
                              disabled={clearSuspensionMutation.isPending || terminateMutation.isPending}
                            >
                              {terminateMutation.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null}
                              Terminate Account
                            </Button>
                          </>
                        ) : alert.alertType === "tin_mismatch" ? (
                          <>
                            {alert.metadata?.userEmail && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                                asChild
                              >
                                <a
                                  href={`mailto:${String(alert.metadata.userEmail)}?subject=Action Required: Please Correct Your Tax ID (SSN/ITIN)&body=Dear ${String(alert.metadata.displayName ?? alert.metadata.legalFirstName ?? "")},\n\nWe recently submitted a TIN Matching request to the IRS and received an error for the Tax ID (SSN/ITIN) on file for your account.\n\nPlease log in to your DebtBridge account and update your SSN/ITIN so that we can correctly file your Form 1099-C.\n\nIRS error code: ${String(alert.metadata.resultCode ?? "")} — ${String(alert.metadata.resultDescription ?? "")}\n\nIf you have questions, please reply to this email.\n\nDebtBridge Foundation`}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  Contact Debtor
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => resolveMutation.mutate({ id: alert.id, notes: resolveNotes[alert.id] })}
                              disabled={resolveMutation.isPending}
                              className="gap-1.5"
                            >
                              {resolveMutation.isPending ? (
                                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              )}
                              Mark Resolved
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => resolveMutation.mutate({ id: alert.id, notes: resolveNotes[alert.id] })}
                            disabled={resolveMutation.isPending}
                            className="gap-1.5"
                          >
                            {resolveMutation.isPending ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            )}
                            Mark Resolved
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <QuoteCarousel />

    </div>
  );
}
